import { useState, type ReactElement } from 'react';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildSuiteDetailPath } from '@/features/shared/components/tableSystem/tableRoutes';
import { getTableV2LinkRoutingProps } from '@/features/shared/components/tableSystem/TableV2LinkRouting';
import { tableV2NestedDepthSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2NestedCollapse,
  TableV2SortableHeader,
  TableV2TagGroup,
  tableV2ChevronCellSx,
  tableV2NestedBodyRowHeightPx,
  tableV2NestedHeaderHeight,
  tableV2NamePrimaryFontSize,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { resolveEntityTags } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { iconSizes, s, typographyTokens } from '@/theme/tokens';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  Box,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  resolveStatusChipLabel,
} from '@/ui/primitives';
import { KeyboardArrowDownIcon } from '@/ui/primitives/icons';
import {
  countTestSummaries,
  defaultNestedTestRunSort,
  getSummaryName,
  sortTestRunSummaries,
  type SortableNestedTestRunField,
} from './ExecutionRunsTableV2NestedSorting';
import {
  PassRateCell,
  StatusChip,
  formatUploadedAt,
  nestedCarrierRowSx,
  nestedDetailCellSx,
  nestedDetailContentSx,
  nestedTestTableFrameSx,
  nestedTestTableSurfaceSx,
  relativeTimeSx,
} from './ExecutionRunsTableV2RowShared';
import { TestRunDetailRow } from './ExecutionRunsTableV2TestRunDetailRow';
import type { TagColor } from '@/shared/data/tags';
import type {
  ExecutionRunDto,
  SuiteRunDto,
  SuiteRunSummaryDto,
  TestRunDto,
  TestRunSummaryDto,
} from '@flowqa/shared';

const formatNestedRunNumber = (runNumber: number | null): string | null =>
  runNumber === null ? null : `#${String(runNumber)}`;

const resolveSummaryTimestamp = (
  timestamp: Date | null | undefined,
  parent: ExecutionRunDto,
): Date =>
  timestamp ?? parent.startedAt ?? parent.finishedAt ?? parent.resultsFileCreatedAt ?? new Date(0);

const buildSuiteRunFromSummary = (
  summary: SuiteRunSummaryDto,
  parent: ExecutionRunDto,
): SuiteRunDto | null => {
  if (!summary.executionId) return null;
  const timestamp = resolveSummaryTimestamp(summary.timestamp, parent);
  return {
    id: summary.executionId,
    runId: parent.id,
    resultsFileId: parent.resultsFileId ?? null,
    suiteId: summary.suiteId,
    runNumber: summary.runNumber ?? null,
    suiteName: summary.suiteName ?? null,
    displayName: summary.displayName ?? null,
    suiteSlug: summary.suiteSlug ?? null,
    tags: summary.tags ?? [],
    projectId: parent.projectId,
    projectName: parent.projectName ?? null,
    projectSlug: parent.projectSlug ?? null,
    organisationId: parent.organisationId,
    createdById: parent.createdById,
    status: summary.status ?? 'untested',
    timestamp,
    durationMs: summary.durationMs ?? null,
    createdAt: parent.resultsFileCreatedAt ?? timestamp,
    deletedAt: null,
  };
};

export const SuiteRunDetailRow = ({
  deleteRowPendingId,
  editRowPendingId,
  isLastSuite,
  parent,
  tagColorByName,
  testRunDeleteRowPendingId,
  testRunEditRowPendingId,
  suite,
  tests,
  onDelete,
  onDeleteTestRun,
  onEdit,
  onEditTestRun,
}: {
  deleteRowPendingId: string | null;
  editRowPendingId: string | null;
  isLastSuite: boolean;
  parent: ExecutionRunDto;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunDeleteRowPendingId: string | null;
  testRunEditRowPendingId: string | null;
  suite: SuiteRunSummaryDto;
  tests: TestRunSummaryDto[];
  onDelete: (row: SuiteRunDto) => void;
  onDeleteTestRun: (row: TestRunDto) => void;
  onEdit: (row: SuiteRunDto) => void;
  onEditTestRun: (row: TestRunDto) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const [testRunSort, setTestRunSort] = useState<{
    field: SortableNestedTestRunField;
    direction: 'asc' | 'desc';
  }>(defaultNestedTestRunSort);
  const suiteName = getSummaryName(suite);
  const suiteRunNumber = suite.runNumber ?? null;
  const suiteRunNumberLabel = formatNestedRunNumber(suiteRunNumber);
  const suiteHref = buildSuiteDetailPath(suite.suiteSlug ?? suite.suiteId);
  const suiteRunHref =
    suite.suiteSlug && suiteRunNumber !== null
      ? `/suites/${suite.suiteSlug}/runs/${suiteRunNumber}`
      : null;
  const counts = countTestSummaries(tests);
  const ranAt = suite.timestamp ?? null;
  const ranAtLabel = formatRelativeTime(ranAt);
  const ranAtAbsoluteLabel = formatUploadedAt(ranAt);
  const durationLabel = formatDuration(suite.durationMs ?? null);
  const tags = resolveEntityTags(suite);
  const suiteRun = buildSuiteRunFromSummary(suite, parent);
  const sortedTests = sortTestRunSummaries(tests, testRunSort);

  const handleTestRunSortChange = (field: SortableNestedTestRunField): void => {
    setTestRunSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  return (
    <>
      <TableRow
        sx={[
          tableV2NestedDepthSx[1].bodyRow,
          {
            '& > td': {
              borderBottom:
                expanded || isLastSuite ? 0 : (theme) => `1px solid ${theme.app.colors.border}`,
              boxSizing: 'border-box',
              height: `${String(tableV2NestedBodyRowHeightPx)}px`,
              maxHeight: `${String(tableV2NestedBodyRowHeightPx)}px`,
              overflow: 'hidden',
              py: s('sm'),
              verticalAlign: 'middle',
            },
          },
        ]}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <IconButton
            aria-label={expanded ? 'Collapse suite tests' : 'Expand suite tests'}
            aria-expanded={expanded}
            size="small"
            onClick={() => {
              setExpanded((current) => !current);
            }}
            sx={{
              alignItems: 'center',
              color: 'text.primary',
              display: 'inline-flex',
              height: s('2xl'),
              justifyContent: 'center',
              left: '50%',
              lineHeight: 0,
              p: s('none'),
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              verticalAlign: 'middle',
              width: s('2xl'),
              '& > span': {
                height: iconSizes.lg,
                width: iconSizes.lg,
              },
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: (theme) => theme.transitions.create('transform', { duration: 160 }),
              }}
            />
          </IconButton>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              alignContent: 'center',
              display: 'grid',
              gap: s('none'),
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Text
              role="body"
              component="div"
              sx={{
                fontSize: tableV2NamePrimaryFontSize,
                fontWeight: typographyTokens.body.fontWeight,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {suiteHref ? (
                <OverflowTooltip title={suiteName} inline>
                  <Link
                    {...getTableV2LinkRoutingProps(suiteHref)}
                    sx={{
                      color: 'text.primary',
                      display: 'inline-block',
                      fontSize: tableV2NamePrimaryFontSize,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textDecorationColor: 'currentColor',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {suiteName}
                  </Link>
                </OverflowTooltip>
              ) : (
                suiteName
              )}
            </Text>
            {suiteRunNumberLabel ? (
              <Text
                role="captionSmall"
                color="textSecondary"
                component="div"
                sx={{
                  fontSize: (theme) => theme.typography.caption.fontSize,
                  lineHeight: 1.2,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {suiteRunHref ? (
                  <Link
                    {...getTableV2LinkRoutingProps(suiteRunHref)}
                    sx={{
                      color: 'text.secondary',
                      display: 'inline-block',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textDecorationColor: 'currentColor',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {suiteRunNumberLabel}
                  </Link>
                ) : (
                  suiteRunNumberLabel
                )}
              </Text>
            ) : null}
          </Box>
        </TableCell>
        <TableCell>
          <StatusChip
            status={suite.status ?? 'untested'}
            label={resolveStatusChipLabel(suite.status ?? 'untested')}
          />
        </TableCell>
        <TableCell>
          <PassRateCell counts={counts} />
        </TableCell>
        <TableCell>
          <Text role="body" color="textSecondary">
            {durationLabel.length > 0 ? durationLabel : '-'}
          </Text>
        </TableCell>
        <TableCell>
          <Box sx={{ alignContent: 'center', display: 'grid', gap: s('none') }}>
            <Text
              role="body"
              color="textSecondary"
              component="div"
              sx={{
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ranAtAbsoluteLabel}
            </Text>
            <Text
              role="captionSmall"
              component="div"
              sx={[
                relativeTimeSx,
                {
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              ]}
            >
              {ranAtLabel || '-'}
            </Text>
          </Box>
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={tags} />
        </TableCell>
        <TableCell align="center">
          <TableActionsCell
            rowName={suiteName}
            verticalAlign="center"
            disabledTooltip={suiteRun ? null : 'Actions unavailable for this suite run.'}
            editActionLabel={getEntityActionLabel('edit', 'Suite Run')}
            deleteActionLabel={getEntityActionLabel('delete', 'Suite Run')}
            onEdit={
              suiteRun
                ? () => {
                    onEdit(suiteRun);
                  }
                : undefined
            }
            onDelete={
              suiteRun
                ? () => {
                    onDelete(suiteRun);
                  }
                : undefined
            }
            disableEdit={
              suiteRun
                ? editRowPendingId === suiteRun.id || deleteRowPendingId === suiteRun.id
                : true
            }
            disableDelete={suiteRun ? deleteRowPendingId === suiteRun.id : true}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          nestedCarrierRowSx,
          expanded && !isLastSuite
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell
          colSpan={8}
          sx={
            expanded
              ? [
                  nestedDetailCellSx,
                  {
                    borderBottom: isLastSuite ? 0 : undefined,
                  },
                ]
              : { borderBottom: 0, p: s('none') }
          }
        >
          <TableV2NestedCollapse expanded={expanded}>
            <Box sx={nestedDetailContentSx}>
              {sortedTests.length > 0 ? (
                <Box sx={nestedTestTableFrameSx}>
                  <Table
                    aria-label="Test runs"
                    sx={[
                      nestedTestTableSurfaceSx,
                      {
                        tableLayout: 'fixed',
                        '& th': {
                          borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                          color: 'text.primary',
                          fontSize: typographyTokens.body.fontSize,
                          fontWeight: typographyTokens.body.fontWeight,
                          height: tableV2NestedHeaderHeight,
                          py: s('none'),
                          verticalAlign: 'middle',
                        },
                        '& tbody tr:last-of-type > td': {
                          borderBottom: 0,
                        },
                        '& td': {
                          fontSize: typographyTokens.body.fontSize,
                        },
                        '& .MuiTypography-root': {
                          fontSize: typographyTokens.body.fontSize,
                        },
                      },
                    ]}
                  >
                    <colgroup>
                      <col width="30%" />
                      <col width="13%" />
                      <col width="13%" />
                      <col width="20%" />
                      <col width="20%" />
                      <col width="4%" />
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableV2SortableHeader
                            activeSort={testRunSort}
                            field="runNumber"
                            label="Test Run"
                            onSortChange={handleTestRunSortChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TableV2SortableHeader
                            activeSort={testRunSort}
                            field="status"
                            label="Status"
                            onSortChange={handleTestRunSortChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TableV2SortableHeader
                            activeSort={testRunSort}
                            field="durationMs"
                            label="Duration"
                            onSortChange={handleTestRunSortChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TableV2SortableHeader
                            activeSort={testRunSort}
                            field="timestamp"
                            label="Ran At"
                            onSortChange={handleTestRunSortChange}
                          />
                        </TableCell>
                        <TableCell>Tags</TableCell>
                        <TableCell
                          align="center"
                          sx={{ height: tableV2NestedHeaderHeight, p: s('none') }}
                        >
                          <TableV2ActionsHeaderIcon />
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedTests.map((test) => (
                        <TestRunDetailRow
                          key={`${test.testId}:${test.executionId ?? ''}`}
                          deleteRowPendingId={testRunDeleteRowPendingId}
                          editRowPendingId={testRunEditRowPendingId}
                          parent={parent}
                          suiteRunId={suite.executionId ?? null}
                          suiteSlug={suite.suiteSlug ?? null}
                          tagColorByName={tagColorByName}
                          test={test}
                          onDelete={onDeleteTestRun}
                          onEdit={onEditTestRun}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Text role="body" color="textSecondary">
                  No tests available for this suite.
                </Text>
              )}
            </Box>
          </TableV2NestedCollapse>
        </TableCell>
      </TableRow>
    </>
  );
};
